import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, ChevronDown, ChevronRight, Info, Loader2, X } from 'lucide-react';
import { apiCall } from '@/lib/api-client';
import { bugReportsApi } from '@/features/bug-reports/bug-reports.api';
import type { BugReport, BugReportStatus } from '@/types/api';
import { Skeleton } from '@/components/chassis/States';
import { toast } from '@/stores/toast.store';
import { useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * F5-T6c · Admin 10 (sección 4) — «Reportar un problema», el otro lado de la
 * bandeja: lo que ve el rol operativo (agente/navigator/uploader) desde la
 * barra superior. Tres campos y nada más — un formulario largo hace que nadie
 * reporte.
 *
 * MUERE por contrato: la severidad numérica (escala de 4 opciones) →
 * «¿Te bloquea?» con dos pills. El server sigue pidiendo `severity`, así que
 * el mapeo honesto es: «Me bloquea» → 'blocking' · «Puedo seguir» → 'medium'
 * (cero cambios de API).
 *
 * SE CONSERVA (contrato: «el rol operativo conserva visibilidad del estado de
 * sus propios reportes» + precedente E-23): la lista «mis reportes» dentro del
 * modal, incluida la nota de resolución que el server ya guarda (control vivo,
 * ni dibujado ni matado).
 *
 * El TRIGGER de la barra no se toca: los shells lo montan y lo estilan.
 */

const PAGE_SIZE = 5;

type UiStatus = 'new' | 'review' | 'resolved' | 'nobug';

const STATUS_TO_UI: Record<BugReportStatus, UiStatus> = {
  open: 'new',
  triaged: 'review',
  resolved: 'resolved',
  wontfix: 'nobug',
};

const STATUS_LABEL_KEY: Record<UiStatus, StringKey> = {
  new: 'f5t6c.bugs.status.new',
  review: 'f5t6c.bugs.status.review',
  resolved: 'f5t6c.bugs.status.resolved',
  nobug: 'f5t6c.bugs.status.nobug',
};

const STATUS_CHIP: Record<UiStatus, string> = {
  new: 'bg-nav-amberTint text-nav-amberText border-[#e6cf9a]',
  review: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  resolved: 'bg-[#eef4ee] text-nav-sageText border-nav-sage',
  nobug: 'bg-nav-hair text-nav-slate border-nav-card',
};

export function BugReportButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 min-h-[44px] min-w-[44px] justify-center px-2 rounded-lg text-sm text-nav-slate hover:text-nav-red hover:bg-nav-redTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-red focus-visible:ring-offset-2"
        aria-label="Report an issue"
        title="Report an issue"
      >
        <Bug className="h-4 w-4" />
        <span className="sr-only sm:not-sr-only sm:text-xs">Report</span>
      </button>
      {open && <BugReportModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function BugReportModal({ onClose }: { onClose: () => void }): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [what, setWhat] = useState('');
  const [more, setMore] = useState('');
  // Default dibujado: «Me bloquea» seleccionado.
  const [blocks, setBlocks] = useState(true);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const submit = useMutation({
    mutationFn: () =>
      apiCall<{ id: string }>('/bug-reports', {
        method: 'POST',
        body: {
          // Dos campos del dibujo, un solo `message` del server: la primera
          // línea es el título que lee la bandeja (Admin 10).
          message: more.trim().length > 0 ? `${what.trim()}\n\n${more.trim()}` : what.trim(),
          severity: blocks ? 'blocking' : 'medium',
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        },
      }),
    onSuccess: () => {
      toast.success(t('f5t6c.bugs.form.sentTitle'), t('f5t6c.bugs.form.sentDesc'));
      queryClient.invalidateQueries({ queryKey: ['bug-reports', 'mine'] });
      setWhat('');
      setMore('');
      setBlocks(true);
    },
  });

  const canSubmit = what.trim().length >= 5 && !submit.isPending;
  const inputCls =
    'w-full box-border min-h-[44px] px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[14px] text-nav-navy bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bug-modal-title"
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 animate-fade-in motion-reduce:animate-none"
    >
      <div className="absolute inset-0 bg-nav-navy/40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative max-h-[85vh] w-full max-w-[560px] flex flex-col rounded-[12px] border-[1.5px] border-nav-card bg-white overflow-hidden shadow-elevated">
        <header className="sticky top-0 bg-white border-b border-nav-hair px-7 py-5 flex items-center justify-between z-10">
          <h2 id="bug-modal-title" className="font-head text-[18px] font-bold text-nav-navy m-0">
            {t('f5t6c.bugs.form.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('f5t6c.bugs.form.cancel')}
            className="w-11 h-11 min-h-[44px] inline-flex items-center justify-center rounded-lg text-nav-slate hover:bg-nav-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            <X className="h-[18px] w-[18px]" aria-hidden />
          </button>
        </header>

        <div className="modal-scroll flex-1 overflow-y-auto">
          <div ref={dialogRef} tabIndex={-1} className="px-7 py-5 space-y-5">
            <label className="block">
              <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1.5">
                {t('f5t6c.bugs.form.what')}
              </span>
              <input
                type="text"
                value={what}
                onChange={(e) => setWhat(e.target.value)}
                placeholder={t('f5t6c.bugs.form.whatPh')}
                maxLength={200}
                className={inputCls}
              />
            </label>

            <label className="block">
              <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1.5">
                {t('f5t6c.bugs.form.more')}
              </span>
              <textarea
                value={more}
                onChange={(e) => setMore(e.target.value)}
                placeholder={t('f5t6c.bugs.form.morePh')}
                maxLength={4000}
                rows={4}
                className={cn(inputCls, 'py-2.5 resize-y')}
              />
            </label>

            <div>
              <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-[9px]">
                {t('f5t6c.bugs.form.blocks')}
              </p>
              <div className="flex items-center gap-2">
                {[
                  { v: true, label: t('f5t6c.bugs.form.blocksYes') },
                  { v: false, label: t('f5t6c.bugs.form.blocksNo') },
                ].map((opt) => {
                  const active = blocks === opt.v;
                  return (
                    <button
                      key={String(opt.v)}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setBlocks(opt.v)}
                      className={cn(
                        'inline-flex items-center min-h-[44px] px-4 rounded-lg border-[1.5px] font-head text-[13px] font-bold transition-colors duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                        active
                          ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                          : 'bg-white border-nav-line text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Aviso de auto-adjuntos + regla PHI (verbatim, negrita incluida). */}
            <div className="flex items-start gap-2.5 bg-nav-tealTint border-[1.5px] border-[#c9e2e2] rounded-[12px] py-[15px] px-[17px] text-nav-tealDark">
              <Info className="h-[17px] w-[17px] flex-none mt-px" aria-hidden />
              <p className="font-body text-[13.5px] leading-relaxed m-0">
                {t('f5t6c.bugs.form.auto')}
                <strong className="font-bold">{t('f5t6c.bugs.form.autoPhi')}</strong>
                {t('f5t6c.bugs.form.autoTail')}
              </p>
            </div>

            {submit.error && (
              <p role="alert" className="font-body text-[13px] text-nav-red m-0">
                {t('f5t6c.bugs.form.error')}: {(submit.error as Error).message}
              </p>
            )}

            <hr className="border-nav-hair" />

            <MySubmissions t={t} />
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-nav-hair px-7 py-4 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={submit.isPending}
            className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5t6c.bugs.form.cancel')}
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={!canSubmit}
            className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            {submit.isPending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />}
            {submit.isPending ? t('f5t6c.bugs.form.sending') : t('f5t6c.bugs.form.send')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function MySubmissions({ t }: { t: ReturnType<typeof useT> }): JSX.Element {
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const q = useQuery({
    queryKey: ['bug-reports', 'mine'],
    queryFn: () => bugReportsApi.mine(50),
    staleTime: 15_000,
  });

  const all = q.data ?? [];
  const visible = all.slice(0, pageSize);
  const hasMore = all.length > visible.length;

  return (
    <section aria-labelledby="bug-history-title" className="space-y-2">
      <h3 id="bug-history-title" className="font-head text-[13.5px] font-bold text-nav-navy m-0">
        {t('bugReport.history.title')}
      </h3>

      {q.isLoading ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[52px] w-full" />
          ))}
        </div>
      ) : all.length === 0 ? (
        <p className="font-body text-[12.5px] text-nav-slate py-2 m-0">{t('bugReport.history.empty')}</p>
      ) : (
        <>
          <ul className="divide-y divide-nav-hair border-[1.5px] border-nav-card rounded-[10px] overflow-hidden">
            {visible.map((row) => (
              <SubmissionRow
                key={row.id}
                row={row}
                expanded={!!expandedIds[row.id]}
                onToggle={() => setExpandedIds((m) => ({ ...m, [row.id]: !m[row.id] }))}
                t={t}
              />
            ))}
          </ul>

          {hasMore && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setPageSize((n) => n + PAGE_SIZE)}
                className="min-h-[44px] px-3 font-body text-[12.5px] text-nav-tealDark hover:bg-[#f4fafa] transition-colors rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                {t('bugReport.history.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

interface SubmissionRowProps {
  row: BugReport;
  expanded: boolean;
  onToggle: () => void;
  t: ReturnType<typeof useT>;
}

function SubmissionRow({ row, expanded, onToggle, t }: SubmissionRowProps): JSX.Element {
  const ui = STATUS_TO_UI[row.status];
  const blocking = row.severity === 'blocking';
  const firstLine = row.message.split('\n')[0] ?? row.message;
  const truncated = firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;

  return (
    <li className="bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3.5 py-2.5 min-h-[52px] flex items-start gap-2 hover:bg-[#f4fafa] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:-outline-offset-2"
        aria-expanded={expanded}
      >
        <span className="text-nav-faint mt-0.5 shrink-0" aria-hidden>
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>
        <span className="flex-1 min-w-0 space-y-1">
          <span className="block font-body text-[11.5px] text-nav-slate tabular-nums">
            {formatRelativeTime(new Date(row.createdAt))}
          </span>
          <span className="block font-body text-[13px] font-semibold text-nav-navy break-words">{truncated}</span>
          <span className="flex items-center gap-1.5 flex-wrap">
            {blocking && (
              <span className="inline-flex items-center rounded-full border border-[#e6cf9a] bg-nav-amberTint text-nav-amberText px-2 py-0.5 font-head text-[10.5px] font-bold">
                {t('f5t6c.bugs.form.blocksYes')}
              </span>
            )}
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 font-head text-[10.5px] font-bold',
                STATUS_CHIP[ui],
              )}
            >
              {t(STATUS_LABEL_KEY[ui])}
            </span>
          </span>
        </span>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3 pt-0 ml-5 space-y-2 font-body text-[12px] text-nav-navy">
          <div>
            <p className="text-nav-slate mb-0.5">{t('bugReport.history.expand.message')}:</p>
            <p className="whitespace-pre-wrap break-words m-0">{row.message}</p>
          </div>

          {row.pageUrl && (
            <div>
              <p className="text-nav-slate mb-0.5">{t('bugReport.history.expand.page')}:</p>
              <p className="break-all tabular-nums text-[11.5px] m-0">{row.pageUrl}</p>
            </div>
          )}

          {/* Conservado (E-23): la nota de resolución que deja el equipo —
              visibilidad del estado propio, contrato «se conserva». */}
          <div>
            <p className="text-nav-slate mb-0.5">{t('bugReport.history.expand.resolution')}:</p>
            {row.resolutionNote ? (
              <p className="whitespace-pre-wrap break-words m-0">{row.resolutionNote}</p>
            ) : (
              <p className="italic text-nav-slate m-0">{t('bugReport.history.expand.noResolution')}</p>
            )}
          </div>

          {row.resolvedByName && (
            <p className="text-nav-slate m-0">
              {t('bugReport.history.expand.resolvedBy')}: <span className="text-nav-navy">{row.resolvedByName}</span>
              {row.resolvedAt && (
                <>
                  {' '}
                  · {t('bugReport.history.expand.resolvedAt')}: {formatRelativeTime(new Date(row.resolvedAt))}
                </>
              )}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

// Type-export to silence strict no-unused on imports above used only in types.
export type { BugReportStatus };
